/**
 * Reserved ENS Subdomain Names
 *
 * These names are protected from registration to prevent:
 * - Brand impersonation
 * - Technical conflicts
 * - Abuse vectors
 * - Future feature collisions
 *
 * Validation is applied both at the application level (frontend + backend)
 * and should eventually be enforced on-chain via SubdomainRegistrar contract.
 */

// Brand and official names
const BRAND_NAMES = [
  'fairdrop',
  'fairdrive',
  'fds',
  'fairdatasociety',
  'fair-data-society',
  'official',
  'support',
  'help',
  'admin',
  'team',
  'news',
  'updates',
  'announcements',
  'security',
  'swarm',
  'ethswarm',
];

// Technical/system names
const TECHNICAL_NAMES = [
  'api',
  'app',
  'www',
  'mail',
  'ftp',
  'smtp',
  'imap',
  'pop',
  'dns',
  'ns1',
  'ns2',
  'cdn',
  'static',
  'assets',
  'null',
  'undefined',
  'root',
  'system',
  'test',
  'demo',
  'example',
  'sample',
  'staging',
  'dev',
  'development',
  'prod',
  'production',
  'localhost',
  'internal',
  'private',
];

// Common abuse target names
const ABUSE_TARGET_NAMES = [
  'administrator',
  'moderator',
  'mod',
  'abuse',
  'postmaster',
  'webmaster',
  'hostmaster',
  'billing',
  'sales',
  'info',
  'contact',
  'legal',
  'compliance',
  'privacy',
  'dmca',
  'copyright',
  'noreply',
  'no-reply',
  'donotreply',
  'mailer-daemon',
];

// Future feature names
const FUTURE_FEATURE_NAMES = [
  'escrow',
  'vault',
  'safe',
  'backup',
  'share',
  'public',
  'files',
  'storage',
  'business',
  'enterprise',
  'pro',
  'premium',
  'plus',
  'marketplace',
  'exchange',
  'wallet',
  'pay',
  'payment',
  'payments',
];

// Protocol and standard names
const PROTOCOL_NAMES = [
  'ens',
  'eth',
  'ethereum',
  'bitcoin',
  'btc',
  'crypto',
  'web3',
  'ipfs',
  'bzz',
  'onion',
  'tor',
];

// Short names that could cause confusion (1-2 chars)
const SHORT_NAMES = [
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm',
  'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  'me', 'my', 'us', 'we', 'id', 'io', 'co', 'go', 'do', 'no', 'ok',
];

// Combined set for efficient lookup
export const RESERVED_NAMES = new Set([
  ...BRAND_NAMES,
  ...TECHNICAL_NAMES,
  ...ABUSE_TARGET_NAMES,
  ...FUTURE_FEATURE_NAMES,
  ...PROTOCOL_NAMES,
  ...SHORT_NAMES,
]);

/**
 * Check if a name is reserved
 * @param name - The subdomain name to check (without .fairdrop.eth suffix)
 * @returns true if the name is reserved and cannot be registered
 */
export function isReservedName(name: string): boolean {
  if (!name) return true;
  const normalized = name.toLowerCase().trim();
  return RESERVED_NAMES.has(normalized);
}

/**
 * Get the reason why a name is reserved (for error messages)
 * @param name - The subdomain name to check
 * @returns Description of why it's reserved, or null if not reserved
 */
export function getReservedReason(name: string): string | null {
  if (!name) return 'Name cannot be empty';

  const normalized = name.toLowerCase().trim();

  if (BRAND_NAMES.includes(normalized)) {
    return 'This name is reserved for official Fairdrop/FDS use';
  }
  if (TECHNICAL_NAMES.includes(normalized)) {
    return 'This name is reserved for technical purposes';
  }
  if (ABUSE_TARGET_NAMES.includes(normalized)) {
    return 'This name is reserved to prevent impersonation';
  }
  if (FUTURE_FEATURE_NAMES.includes(normalized)) {
    return 'This name is reserved for future features';
  }
  if (PROTOCOL_NAMES.includes(normalized)) {
    return 'This name is reserved as a protocol/standard name';
  }
  if (SHORT_NAMES.includes(normalized)) {
    return 'Names must be at least 3 characters long';
  }

  return null;
}

/**
 * Validate a username for registration
 * Checks format and reserved names
 * @param name - The subdomain name to validate
 * @returns { valid: boolean, error?: string }
 */
export function validateUsername(name: string): { valid: boolean; error?: string } {
  if (!name) {
    return { valid: false, error: 'Username is required' };
  }

  const normalized = name.toLowerCase().trim();

  // Length check (3-32 characters)
  if (normalized.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }
  if (normalized.length > 32) {
    return { valid: false, error: 'Username must be 32 characters or less' };
  }

  // Format check: alphanumeric and hyphens, no leading/trailing hyphens
  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(normalized) && normalized.length > 1) {
    return { valid: false, error: 'Username can only contain letters, numbers, and hyphens (not at start/end)' };
  }
  if (normalized.length === 1 && !/^[a-z0-9]$/.test(normalized)) {
    return { valid: false, error: 'Invalid username format' };
  }

  // No consecutive hyphens
  if (normalized.includes('--')) {
    return { valid: false, error: 'Username cannot contain consecutive hyphens' };
  }

  // Reserved name check
  const reservedReason = getReservedReason(normalized);
  if (reservedReason) {
    return { valid: false, error: reservedReason };
  }

  return { valid: true };
}

// For CommonJS compatibility (backend)
export default {
  RESERVED_NAMES,
  isReservedName,
  getReservedReason,
  validateUsername,
};
